import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMatch } from 'react-router';
import { PATH_PREVIEW_FORM } from '../../../constants/paths';
import {
  SHORTTEXT,
  NUMBER,
  SELECT,
  RANGE,
  SWITCH,
  SWITCHTOGGLE,
  RICHTEXT,
  EMAIL,
  LINK,
  MCQ,
  GPSCOORDINATES,
  PHONENUMBER,
  URL,
  ADVANCEDSELECT,
  DATETIMERANGE,
  DATETIME,
  ADDRESS,
  FILEUPLOAD,
  RIB,
} from '../../../constants/typology/fieldType';
import {
  TEXT as DATA_TEXT,
  NUM as DATA_NUM,
  VIEWER,
  LINK as DATA_LINK,
  VIDEO as DATA_VIDEO,
  IFRAME as DATA_IFRAME,
  PDF_OCR as DATA_PDF_OCR,
  AUDIO,
  IMAGE,
} from '../../../constants/typology/dataType';
import TextField from '../Field/Text/TextField';
import NumberField from '../Field/Number/NumberField';
import SelectField from '../Field/Select/SelectField';
import RangeField from '../Field/Range/RangeField';
import SwitchField from '../Field/Switch/SwitchField';
import SwitchToggleField from '../Field/SwitchToggle/SwitchToggleField';
import RichTextField from '../Field/RichText/RichTextField';
import EmailField from '../Field/Email/EmailField';
import LinkField from '../Field/Link/LinkField';
import GPSCoordinatesField from '../Field/GPSCoordinates/GPSCoordinatesField';
import TextData from '../Data/Text/TextData';
import PhoneField from '../Field/Phone/PhoneField';
import UrlField from '../Field/Url/UrlField';
import LinkData from '../Data/Link/LinkData';
import ViewerData from '../Data/Viewer/ViewerData';
import VideoPlayerData from '../Data/VideoPlayer/VideoPlayerData';
import MCQField from '../Field/MCQ/MCQField';
import createValidator from '../../../helpers/validate';
import AdvancedSelectField from '../AdvancedSelectField/AdvancedSelectField';
import AudioPlayerData from '../Data/AudioPlayer/AudioPlayerData';
import ImageData from '../Data/Image/ImageData';
import DatetimeRangeField from '../Field/DatetimeRange/DatetimeRangeField';
import DatetimeField from '../Field/Datetime/DatetimeField';
import AddressField from '../Field/Address/AddressField';
import UploadField from '../Field/Upload/UploadField';
import IframeData from '../Data/Iframe/IframeData';
import RibField from '../Field/Rib/RibField';
import PdfOcrData from '../Data/PdfOcr/PdfOcrData';

const componentMap = {
  [SHORTTEXT]: memo(TextField),
  [NUMBER]: memo(NumberField),
  [SELECT]: memo(SelectField),
  [RANGE]: memo(RangeField),
  [SWITCH]: memo(SwitchField),
  [SWITCHTOGGLE]: memo(SwitchToggleField),
  [RICHTEXT]: memo(RichTextField),
  [EMAIL]: memo(EmailField),
  [MCQ]: memo(MCQField),
  [LINK]: memo(LinkField),
  [DATA_TEXT]: memo(TextData),
  [DATA_NUM]: memo(TextData),
  [VIEWER]: memo(ViewerData),
  [DATA_LINK]: memo(LinkData),
  [DATA_VIDEO]: memo(VideoPlayerData),
  [DATA_IFRAME]: memo(IframeData),
  [DATA_PDF_OCR]: memo(PdfOcrData),
  [GPSCOORDINATES]: memo(GPSCoordinatesField),
  [PHONENUMBER]: memo(PhoneField),
  [URL]: memo(UrlField),
  [ADVANCEDSELECT]: memo(AdvancedSelectField),
  [AUDIO]: memo(AudioPlayerData),
  [IMAGE]: memo(ImageData),
  [DATETIMERANGE]: memo(DatetimeRangeField),
  [DATETIME]: memo(DatetimeField),
  [ADDRESS]: memo(AddressField),
  [FILEUPLOAD]: memo(UploadField),
  [RIB]: memo(RibField),
};

function AdaptableField({ fieldType, validation, label, name, ...props }) {
  const validate = useMemo(() => createValidator(validation, label || name), [
    validation,
    label,
    name,
  ]);

  const isPreview = useMatch(PATH_PREVIEW_FORM) != null;

  const RenderField = componentMap[fieldType] || null;

  return RenderField ? (
    <RenderField
      {...props}
      name={name}
      label={label}
      required={validation && validation.required}
      information={validation ? validation.information : ''}
      validate={validate}
      validation={validation}
      remoteOptions={validation?.remoteOptions}
      isPreview={isPreview}
    />
  ) : null;
}

AdaptableField.propTypes = {
  fieldType: PropTypes.string,
  validation: PropTypes.object,
  label: PropTypes.string,
  name: PropTypes.string,
};

export default AdaptableField;
